<script setup>
import {
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    ListboxLabel,
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/vue';
import {
    PopoverRoot,
    PopoverTrigger,
    PopoverContent,
    Primitive,
} from 'reka-ui';
import { computed, ref, useTemplateRef } from 'vue';
import AppIcon from '../atoms/app/AppIcon.vue';
import FormCheckbox from '../atoms/form/FormCheckbox.vue';

// TODO / Known Issues:

// 1. It would be much, much nicer if all of this was a single reka-ui listbox but their listbox is bugged as of 2.2.0
// when using a filter, see https://github.com/unovue/reka-ui/issues/1808. Therefore, we use a mix of components.

// 2. After opening the popover, the combobox needs an extra keyboard input before it’s treated as open.
// Should be handled by static prop according to the headless ui docs, but it's not.

const props = defineProps({
    options: {
        type: Array,
        default: () => [],
        required: true,
    },
    modelValue: {
        type: String,
    },
    status: {
        type: String,
        default: undefined,
    },
    screenReaderLabel: {
        type: String,
    },
    dynamicPopoverWidth: {
        type: Boolean,
        default: false,
    },
    label: {
        type: String,
    },
});

// model type: { label, value } or [{ label, value }, ...]
const model = defineModel('modelValue', { required: true, type: [Array, Object, null] });

const OPTIONS_NEEDED_TO_RENDER_SEARCH = 10;
const LISTBOX_DEFAULT_LABEL = 'Auswählen...';
const withSearch = computed(() => props.options.length >= OPTIONS_NEEDED_TO_RENDER_SEARCH);
const multiple = computed(() => Array.isArray(model.value));

const listboxLabel = computed(() => {
    if (props.label) {
        return props.label;
    }

    if (multiple.value) {
        return model.value?.toReversed().map(({ label }) => label).join(', ') || LISTBOX_DEFAULT_LABEL;
    }

    return model.value?.label || LISTBOX_DEFAULT_LABEL;
});

// Need controlled state so we can bubble the close event from hitting the escape-key up from combobox to popover
const popoverOpen = ref(false);

const query = ref('');
const filteredOptions = computed(() => (query.value === '' ?
    props.options :
    props.options.filter(option => option.label.toLowerCase().includes(query.value.toLowerCase()))));

function onItemSelect(event) {
    const option = event.detail.value;
    if (multiple.value) {
        const index = model.value?.findIndex(({ value }) => option.value === value) ?? -1;
        if (index !== -1) {
            model.value.splice(index, 1);
        } else {
            model.value.push(option);
        }
    } else {
        model.value = option;
        popoverOpen.value = false;
    }
}

const comboboxInput = useTemplateRef('comboboxInput');
function onReset() {
    query.value = '';
    comboboxInput.value.el.focus();
}

// When closing a combobox that has search functionality
function toggleComplexCombobox(open = undefined) {
    if (open !== undefined) {
        popoverOpen.value = open;
    } else {
        popoverOpen.value = !popoverOpen.value;
    }

    if (!popoverOpen.value) {
        query.value = '';
    }
}

const itemClasses = 'flex justify-start items-center py-s px-m truncate gap-xs cursor-pointer';
const dropdownClasses = 'bg-white w-full absolute mt-xs rounded border ' +
    'border-solid border-gray-300 shadow z-50 max-h-[318px] overflow-y-auto';

function dropdownTriggerClasses(open) {
    return {
        // eslint-disable-next-line max-len,vue/max-len
        "p-m border rounded w-full text-start flex justify-between items-center gap-xs transition-colors": true,
        'border-black': open,
        'border-gray-300': !open && props.status !== 'error',
        'border-error-border has-status-error': props.status === 'error',
    };
}

// Label management for reka-ui popover
const popoverTrigger = useTemplateRef('popoverTrigger');
const labelFor = computed(() => popoverTrigger.value?.$el.id);
</script>
<template>
    <template v-if="withSearch">
        <div class="max-w-full">
            <PopoverRoot
                v-slot="{ open }"
                as="div"
                class="relative"
                :open="popoverOpen"
            >
                <label
                    v-if="screenReaderLabel"
                    class="sr-only"
                    :for="labelFor"
                >{{ screenReaderLabel }} {{ listboxLabel }}</label>
                <PopoverTrigger
                    ref="popoverTrigger"
                    :class="dropdownTriggerClasses(open)"
                    :title="listboxLabel"
                    data-qa="combobox trigger"
                    @click="toggleComplexCombobox()"
                >
                    <span class="truncate">
                        {{ listboxLabel }}
                    </span>
                    <AppIcon
                        name="arrow-down"
                        class="transition-transform shrink-0"
                        :class="open && 'rotate-180'"
                        size="l"
                    />
                </PopoverTrigger>
                <PopoverContent
                    as="div"
                    align="start"
                    :class="dropdownClasses"
                    :style="dynamicPopoverWidth
                        ? 'width: max-content; max-width: 300px' : 'width: var(--reka-popover-trigger-width, 300px)'"
                    @interact-outside="toggleComplexCombobox(false)"
                >
                    <Combobox
                        v-model="model"
                        :multiple
                        @update:model-value="!multiple && toggleComplexCombobox(false)"
                    >
                        <div
                            class="p-s relative :text-bold"
                            @keydown.esc="toggleComplexCombobox(false)"
                        >
                            <div
                                class="border border-solid border-gray-300 py-m px-s
                                 has-[:focus]:border-gray-1200 rounded flex gap-s items-center"
                            >
                                <AppIcon
                                    name="magnifier"
                                    size="l"
                                    class="w-xl h-xl text-gray-600 shrink-0"
                                />
                                <ComboboxInput
                                    ref="comboboxInput"
                                    :display-value="() => query"
                                    class="focus-visible:outline-none w-full"
                                    data-qa="combobox search"
                                    @change="query = $event.target.value"
                                />
                                <button
                                    v-if="query.length"
                                    type="button"
                                    @click="onReset"
                                >
                                    <span class="sr-only">Suchtext zurücksetzen</span>
                                    <AppIcon
                                        name="cross"
                                        size="l"
                                        class="w-l h-l mr-xs text-gray-600 shrink-0"
                                    />
                                </button>
                            </div>
                        </div>
                        <ComboboxOptions static>
                            <ComboboxOption
                                v-for="option in filteredOptions"
                                :key="option.value"
                                v-slot="{ selected, active }"
                                :value="option"
                                data-qa="combobox option"
                                @select.prevent="onItemSelect"
                            >
                                <div
                                    :class="[
                                        itemClasses,
                                        selected && !multiple && 'font-bold',
                                        active && 'bg-gray-50',
                                    ]"
                                    :data-qa="`option ${option.value}`"
                                >
                                    <div v-if="multiple">
                                        <FormCheckbox
                                            :model-value="selected"
                                            class="pointer-events-none"
                                        />
                                    </div>
                                    {{ option.label }}
                                    <AppIcon
                                        v-if="selected && !multiple"
                                        class="ml-auto shrink-0"
                                        name="check"
                                        size="l"
                                    />
                                </div>
                            </ComboboxOption>
                            <p
                                v-if="!filteredOptions.length"
                                class="text-gray-600 p-s"
                                data-qa="combobox validation no matches"
                            >
                                Keine Übereinstimmungen. Hast du dich vielleicht vertippt?
                            </p>
                        </ComboboxOptions>
                    </Combobox>
                </PopoverContent>
            </PopoverRoot>
        </div>
    </template>
    <template v-else>
        <Listbox
            v-slot="{ open }"
            v-model="model"
            :multiple
            as="div"
            class="relative max-w-full"
        >
            <ListboxLabel
                v-if="screenReaderLabel"
                class="sr-only"
            >
                {{ screenReaderLabel }}
            </ListboxLabel>
            <ListboxButton
                :class="dropdownTriggerClasses(open)"
                :title="listboxLabel"
                data-qa="combobox trigger"
            >
                <span class="truncate">
                    {{ listboxLabel }}
                </span>
                <AppIcon
                    name="arrow-down"
                    class="transition-transform shrink-0"
                    :class="open && 'rotate-180'"
                    size="l"
                />
            </ListboxButton>
            <ListboxOptions
                :class="[
                    dropdownClasses,
                    'max-w-full top-full left-0',
                ]"
            >
                <ListboxOption
                    v-for="option in options"
                    :key="option.value"
                    v-slot="{ active, selected }"
                    :value="option"
                    data-qa="combobox option"
                    :as="Primitive"
                >
                    <li
                        :data-qa="`option ${option.value}`"
                        :class="[
                            active && 'bg-gray-50',
                            selected && !multiple && 'font-bold',
                            itemClasses,
                        ]"
                    >
                        <div v-if="multiple">
                            <FormCheckbox
                                :model-value="selected"
                                class="pointer-events-none"
                            />
                        </div>
                        {{ option.label }}
                        <AppIcon
                            v-if="selected && !multiple"
                            class="ml-auto shrink-0"
                            name="check"
                            size="l"
                        />
                    </li>
                </ListboxOption>
            </ListboxOptions>
        </Listbox>
    </template>
</template>
